import { useTheme } from "@mui/material";
import { CallbackDataParams } from "echarts/types/dist/shared";
import _ from "lodash";
import { useCallback, useMemo } from "react";

import { Stack } from "@/Components/v2/atoms/Stack";
import { SatCurveLegendCards } from "@/Components/v2/molecule/LegendCards";
import { MarkerLegends } from "@/Components/v2/molecule/MarkerLegends";
import ChartWithMarkers from "@/Components/v2/organism/ChartWithMarkers/ChartWithMarkers";
import { Config, HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import { CurveType, markerMap } from "@/pages/investment-levels/constants/maps";

import { formatTooltip } from "../../helpers/curveTooltip";
import { useInvestmentLevelsStore } from "../../store/useInvestmentLevelsStore";
import { GraphDataDTO } from "../../types/dto";
import { CurveTypeSelector } from "../Sections/CurveTypeSelector";

interface ChartContainerProps {
  curve: GraphDataDTO;
  config: Config;
}

export interface GroupedCurveData {
  id: string;
  label: string;
  isShown: boolean;
  data: { id: string; isActive: boolean }[];
}

const CHART_TITLE = "Weekly Marketing Spend";

const SingleCurveContainer: React.FC<ChartContainerProps> = ({ curve, config }) => {
  const { comparisonMode } = useInvestmentLevelsStore((state) => state.filters);
  const { curveType } = useInvestmentLevelsStore((state) => state.curves);
  const theme = useTheme();

  const markerLegends = Object.values(markerMap);
  const formatCurrency = config.currency.formatCurrency;

  const legends = markerLegends.map((markerLegend) => {
    const curveTypeData = curve.curveTypes[CurveType.REVENUE];
    const markerData = curveTypeData?.markData?.find((marker) => marker.key === markerLegend.key);

    // Always showing spend and revenue in the legend
    const spend = formatCurrency(Number(markerData?.markCoordinates?.[0]) ?? 0);
    const revenue = formatCurrency(Number(markerData?.markCoordinates?.[1]) ?? 0);

    return {
      key: markerLegend.key,
      label: markerLegend.label,
      color: markerLegend.color,
      spend,
      revenue,
    };
  });

  const curveData = [curve];

  const tooltipFormatter = useCallback(
    (params: CallbackDataParams[]) =>
      formatTooltip({
        theme,
        params,
        graphData: curveData,
        curveType,
        comparisonMode,
        formatCurrency,
        config,
      }),
    [theme, curveData, curveType, comparisonMode, formatCurrency, config]
  );

  const curvesData = useMemo(() => {
    return curveData
      .map((curve) => {
        const curveData = curve.curveTypes[curveType];
        if (!curveData) return null;
        return {
          ...curveData,
          color: curve.color,
          curveCoordinates: curveData.curveCoordinates || [],
          markData: curveData.markData || [],
          id: curve.id + "-" + curveType,
        };
      })
      .filter((curve): curve is NonNullable<typeof curve> => curve !== null); // TODO: reuse this logic in GroupCurveContainer
  }, [curveData, curveType]);

  const revenueFormatterOptions = config.showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;
  const yieldFormatterOptions = config.showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  const xAxisFormatter = (value: number) => formatCurrency(value, HIDE_DECIMAL);
  const yAxisFormatter = (value: number) => {
    if (curveType === CurveType.REVENUE) return formatCurrency(value, revenueFormatterOptions);
    if (curveType === CurveType.YIELD) return formatCurrency(value, yieldFormatterOptions);
    return value.toString();
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <CurveTypeSelector />
        <MarkerLegends markerLegends={markerLegends} />
      </Stack>
      <ChartWithMarkers
        title={CHART_TITLE}
        curves={curvesData}
        markerMap={markerMap}
        tooltipFormatter={tooltipFormatter}
        xAxisFormatter={xAxisFormatter}
        yAxisFormatter={yAxisFormatter}
      />
      <SatCurveLegendCards legends={legends} />
    </>
  );
};

export default SingleCurveContainer;
